import isOnlineStatus from "is-online";
import { defer, from, Observable, of, zip } from "rxjs";
import { catchError, concatMap, map, mergeMap, switchMap, switchMapTo } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ApplicationError } from "../../_errors/application-error";
import {
  Inspection,
  InspectionDetailNew,
  InspectionDetailUpdate,
  InspectionForSharedLock,
  InspectionList,
  InspectionListItem,
  InspectionLocation,
  InspectionPermission,
  InspectionUpdateLocation,
  Inspector
} from "../../_objects/inspection";
import { InspectionReportList } from "../../_objects/inspection-report";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";
import { InspectionSchema, InspectionSchemaLocationUpdate, InspectionSchemaUpdate } from "../../_offline/models/inspection.schema";
import { InspectionImageSchema } from "../../_offline/models/inspectionImage.schema";
import { SubjectSchema } from "../../_offline/models/subject.schema";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";
import { UploadDataType, UploadManagementSchema } from "../../_offline/models/upload-management.schema";
import { GUID } from "../../app.constants";
import { UtilsService } from "../_utils.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { InspectionImageDbService } from "../database/inspection-image-db.service";
import { SubjectDbService } from "../database/subject-db.service";
import { SubjectModelDbService } from "../database/subject-model-db.service";
import { UploadManagementDbService } from "../database/upload-management-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { InspectionContractService } from "../interfaces/inspection-contract.service";
import { NetworkStatusService } from "../network-status.service";
import { InspectionOnlineService } from "../online/inspection-online.service";
import { SubjectFilter } from "../storage.service";
import { InspectionReportOfflineService } from "./inspection-report-offline.service";
import { LoginUserOfflineService } from "./login-user-offline.service";
import { StereoMeasurementImageOfflineService } from "./stereo-measurement-offline.service";
import { SubjectModelOfflineService } from "./subject-model-offline.service";
import { FeatureFlagService } from "../feature-flag/feature-flag.service";
import { FeatureFlagOfflineService } from "../feature-flag/feature-flag.offline.service";
import { AssetModuleClient } from "../asset-module/asset-module-client.service";

export class InspectionItemWrapper extends InspectionListItem {
  inspectorId: string = "";
  reportCount: number = null;
  imageCount: number = null;

  constructor(item: InspectionListItem, inspectorId: string, reportCount: number, imageCount: number) {
    super();
    this.id = item.id;
    this.inspectionDate = item.inspectionDate;
    this.inspectors = item.inspectors;
    this.permission = item.permission;
    this.status = item.status;
    this.subjectModelId = item.subjectModelId;
    this.subjectModelName = item.subjectModelName;
    this.subjectSerialNo = item.subjectSerialNo;
    this.title = item.title;
    this.sharedLockBy = item.sharedLockBy;
    this.isOfflineData = item.isOfflineData;

    this.inspectorId = inspectorId;
    this.reportCount = reportCount;
    this.imageCount = imageCount;
    this.connectorTenantId = item.connectorTenantId;
  }
}

@Injectable({
  providedIn: "root"
})
export class InspectionOfflineService implements InspectionContractService {
  constructor(
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private subjectDbService: SubjectDbService,
    private subjectModelDbService: SubjectModelDbService,
    private loginUserService: LoginUserOfflineService,
    private uploadManagementDbService: UploadManagementDbService,
    private fileSysAccessService: FileSystemAccessService,
    private individualReportDbService: IndividualReportDbService,
    private individualReportOfflineService: InspectionReportOfflineService,
    private networkStatusService: NetworkStatusService,
    private inspectionOnlineService: InspectionOnlineService,
    private utils: UtilsService,
    private subjectModelOfflineService: SubjectModelOfflineService,
    private stereoMeasurementImageOfflineService: StereoMeasurementImageOfflineService,
    private featureFlagService: FeatureFlagOfflineService,
    private assetModuleClient: AssetModuleClient
  ) {}

  get tenantId() {
    return this.loginUserService.tenantId;
  }

  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  private get isAppOnline() {
    return this.networkStatusService.isOnline;
  }

  public getInspections(
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string }
  ): Observable<InspectionList> {
    return zip(
      this.inspectionDbService.getListAll(statusCode, subjectFilter, isOwn, sort),
      this.subjectDbService.getAll(subjectFilter?.subjectModelId, false, false),
      this.subjectModelDbService.getAll(false, false)
    ).pipe(
      switchMap(([inspections, subjects, subjectModels]) => {
        let filtered = inspections;

        if (subjectFilter) {
          filtered = this.applySubjectFilter(inspections, subjects, subjectFilter);
        }

        if (filtered.length === 0) {
          return of(this.buildInspectionList([], 0));
        }

        const reportSubs = filtered.map((insp) => this.individualReportOfflineService.getInspectionReportsByInspectionIds([insp.id], 20, 1));

        return zip(...reportSubs).pipe(
          map((reportsList) => {
            const items = this.buildInspectionItems(filtered, subjects, subjectModels, reportsList, sort);
            return this.buildInspectionList(items, inspections.length);
          })
        );
      })
    );
  }

  private applySubjectFilter(inspections: InspectionSchema[], subjects: SubjectSchema[], subjectFilter: SubjectFilter): InspectionSchema[] {
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      if (!subjectFilter.subjectModelId) return inspections;
      return inspections.filter((insp) => {
        const subject = insp.subject as SubjectSchema | undefined;
        if (!subject) return false;
        if (subject.id === subjectFilter.subjectModelId) return true;
        return subject.assetTypeId === subjectFilter.subjectModelId;
      });
    }
    const filteredSubjectIds = this.filterSubjectAttributes(subjects, subjectFilter);
    return inspections.filter((insp) => filteredSubjectIds.has(insp.subjectId));
  }

  private buildInspectionItems(
    inspections: InspectionSchema[],
    subjects: SubjectSchema[],
    subjectModels: SubjectModelSchema[],
    reportsList: InspectionReportList[],
    sort?: { active: string; direction: string }
  ): InspectionItemWrapper[] {
    return inspections
      .map((insp, index) => {
        const assetModuleEnabled = this.featureFlagService.isFlagEnabled("asset_module")();
        const subject = subjects.find((s) => s.id === InspectionSchema.effectiveAssetId(insp, assetModuleEnabled));
        const subjectModel = subjectModels.find((sm) => sm.id === subject?.subjectModelId);
        const item = this.createInspectionListItem(insp, subjectModel, subject);
        return new InspectionItemWrapper(item, item.id, reportsList[index].items.length, null);
      })
      .sort((a, b) => this.sortInspectionItems(a, b, sort));
  }

  private buildInspectionList(items: InspectionListItem[], total: number): InspectionList {
    const list = new InspectionList();
    list.items = items;
    list.total = total;
    list.page = 1;
    return list;
  }

  private sortInspectionItems(a: InspectionListItem, b: InspectionListItem, sort?: { active: string; direction: string }): number {
    if (!sort) return 0;
    const valA = a[sort.active]?.toUpperCase() ?? "";
    const valB = b[sort.active]?.toUpperCase() ?? "";
    const comparison = valA > valB ? 1 : valA < valB ? -1 : 0;
    return sort.direction.toUpperCase() === "ASC" ? comparison : -comparison;
  }

  private filterSubjectAttributes(subjects: SubjectSchema[], subjectFilter: SubjectFilter) {
    return new Set(
      subjects
        .filter((s) =>
          s.subjectAttributes.some(
            (a) =>
              (!subjectFilter.owner || a.owner === subjectFilter.owner) &&
              (!subjectFilter.country || a.country === subjectFilter.country) &&
              (!subjectFilter.district || a.district === subjectFilter.district) &&
              (!subjectFilter.state || a.state === subjectFilter.state) &&
              (!subjectFilter.site || a.site === subjectFilter.site) &&
              (!subjectFilter.project || a.project === subjectFilter.project)
          )
        )
        .map((s) => s.id)
    );
  }

  private createInspectionListItem(insp: InspectionSchema, subjectModel: SubjectModelSchema, subject: SubjectSchema): InspectionListItem {
    const item = new InspectionListItem();
    item.id = insp.id;
    item.title = insp.title;
    item.subjectModelId = subjectModel?.id;
    item.subjectModelName = subjectModel?.name;
    item.subjectSerialNo = subject?.serialNo;
    item.inspectionDate = insp.inspectionDate;
    item.status = insp.status;
    item.inspectors = JSON.parse(insp.inspectors) as Inspector[];
    item.permission = this.getPermission(insp);
    item.sharedLockBy = insp.sharedLockBy;
    item.isOfflineData = insp.isOfflineData;
    item.connectorTenantId = insp.connectorTenantId;
    return item;
  }

  // IS02 GET
  getInspection(id: string, aliveOnly?: boolean): Observable<Inspection> {
    return this.inspectionDbService.getInspection(id, aliveOnly).pipe(
      map((schema) => {
        const assetModuleEnabled = this.featureFlagService.isFlagEnabled("asset_module")();
        return this.createInspection(schema, InspectionSchema.effectiveAssetId(schema, assetModuleEnabled));
      })
    );
  }

  // IS09 GET
  getCheckedOutInspections(): Observable<Inspection[]> {
    throw new Error("Method cannot be executed in offline mode.");
  }

  // IS03 POST
  addInspection(inspection: InspectionDetailNew): Observable<Inspection> {
    const addSchema = new InspectionSchema();
    const uuid = GUID.generateUUID();

    return this.subjectDbService
      .getOne(inspection.subjectId)
      .pipe(
        switchMap((schema) => (this.featureFlagService.isFlagEnabled("asset_module")() ? this.createSubjectIfNotExist(schema) : of(schema))),
        switchMap((subjectSchema) => {
          // get location version
          const subjectModelLocations = subjectSchema.subjectModel.subjectModelLocations;
          const locationVersion = subjectModelLocations.find((_) => _.isNewestVersion).version;
          const subjectAttributes = subjectSchema.subjectAttributes;
          const attributesVersion = subjectAttributes.find((_) => _.isNewestVersion).version;
          addSchema.setInspectionDetailNew(this.tenantId, inspection, true, uuid, this.userId, locationVersion, attributesVersion);
          // offlineモードの時はデフォルトで共有ロック状態にする
          addSchema.sharedLockBy = this.userId;
          return this.inspectionDbService.getListAll();
        })
      )
      .pipe(
        switchMap((schemas) => {
          const originalTitle = inspection.title;
          let count = 1;
          while (schemas.some((inspectionSchema) => inspectionSchema.title === inspection.title)) {
            inspection.title = `${originalTitle} (${count})`;
            count++;
          }
          if (inspection.title.length > 100) {
            throw new Error("Title exceeds 100 characters.");
          }
          addSchema.title = inspection.title;
          addSchema.liveAssetId = inspection.subjectId;

          return this.inspectionDbService.addInspection(addSchema);
        })
      )
      .pipe(
        switchMap((schema) => {
          this.markLiveAssetAsNonDeletable(inspection.subjectId);
          // アップロード管理テーブルに登録
          const uploadManageSchema = new UploadManagementSchema(schema.id, this.tenantId, UploadDataType.Inspection, true, this.userId);
          return this.uploadManagementDbService.put(uploadManageSchema);
        })
      )
      .pipe(switchMap((schema) => this.getInspection(schema.dataId)));
  }

  private markLiveAssetAsNonDeletable(subjectId: string): void {
    if (!this.featureFlagService.isFlagEnabled("asset_module")()) {
      return;
    }
    this.assetModuleClient.setIsDeletable(subjectId, false).subscribe({
      error: (err) => console.error("Failed to set isDeletable on Asset Module API", err)
    });
  }

  /**
   * Online, the server releases the asset itself once no alive inspection references it (VISOL-3558).
   * Offline and standalone only know the bundled asset module, so the client has to release it here.
   */
  private releaseLiveAssetIfUnused(liveAssetId: string | null): Observable<unknown> {
    if (!liveAssetId || this.isAppOnline || !this.featureFlagService.isFlagEnabled("asset_module")()) {
      return of(null);
    }

    return this.inspectionDbService.getListAll().pipe(
      switchMap((inspections) => {
        if (inspections.some((inspection) => inspection.liveAssetId === liveAssetId)) {
          return of(null);
        }
        return this.assetModuleClient.setIsDeletable(liveAssetId, true);
      }),
      catchError((err) => {
        console.error("Failed to set isDeletable on Asset Module API", err);
        return of(null);
      })
    );
  }

  /**
   *
   * @description This is to keep the database constraint integrity while we complete the asset module integration. Would be remove when completed
   *
   */
  createSubjectIfNotExist(subject: SubjectSchema): Observable<SubjectSchema> {
    const subjectModelSchema = subject.subjectModel;
    return this.subjectModelDbService
      .putOne(subjectModelSchema)
      .pipe(concatMap(() => this.subjectDbService.putOne(subject)))
      .pipe(map(() => subject));
  }

  // IS04 PUT
  updateInspection(inspection: InspectionDetailUpdate): Observable<Inspection> {
    const updateSchema = new InspectionSchemaUpdate();
    let images: InspectionImageSchema[];
    let inspectionSchema: InspectionSchema;
    let oldPath: string;
    let newPath: string;
    let blobPath: string;
    let oldTitle: string;
    let schemas: InspectionSchema[];
    let subjectModel: SubjectModel;
    let oldSubjectId: string;

    return zip(
      this.inspectionImageDbService.getAll(inspection.id),
      this.inspectionDbService.getInspection(inspection.id, true),
      from(this.fileSysAccessService.getSaveBlobFolderPath()),
      this.inspectionDbService.getListAll(),
      this.subjectDbService.getOne(inspection.subjectId)
    )
      .pipe(
        switchMap((values) => {
          // get data
          images = values[0];
          inspectionSchema = values[1];
          oldSubjectId = inspectionSchema.subjectId;
          blobPath = values[2];
          schemas = values[3];
          const subjectSchema = values[4];
          oldTitle = inspectionSchema.title;

          // inspection title rename
          const originalTitle = inspection.title;
          let count = 1;
          while (schemas.some((inspectionSchema) => inspectionSchema.title === inspection.title && inspectionSchema.id !== inspection.id)) {
            inspection.title = `${originalTitle} (${count})`;
            count++;
          }
          if (inspection.title.length > 100) {
            throw new Error("Title exceeds 100 characters.");
          }

          oldPath = blobPath + `inspections/${oldTitle}`;
          newPath = blobPath + `inspections/${inspection.title}`;

          const renameOb = oldPath === newPath ? of(true) : from(this.fileSysAccessService.renameFilePath(oldPath, newPath));

          const ensureSubject$ = this.featureFlagService.isFlagEnabled("asset_module")()
            ? this.createSubjectIfNotExist(subjectSchema)
            : of(subjectSchema);

          return ensureSubject$.pipe(
            switchMap(() => zip(this.subjectModelOfflineService.getSubjectModel(subjectSchema.subjectModelId, inspection.locationVersion), renameOb))
          );
        })
      )
      .pipe(
        switchMap((values: any) => {
          if (values[1] !== true && !values[1].returnValue) {
            if (values[1].error && values[1].error.toString().includes("Error: EPERM: operation not permitted")) {
              throw new ApplicationError("Error: EPERM: operation not permitted");
            } else {
              throw new ApplicationError("Failed rename inspection folder paths.");
            }
          }

          subjectModel = values[0];

          // update inspection
          updateSchema.setInspectionDetailUpdate(this.tenantId, inspection, this.userId);
          return this.inspectionDbService.updateInspection(updateSchema);
        })
      )
      .pipe(
        switchMap((values) => {
          inspectionSchema = values;

          if (oldSubjectId !== inspection.subjectId) {
            this.markLiveAssetAsNonDeletable(inspection.subjectId);
          }

          if (!this.isAppOnline) {
            return of(values);
          }

          // online sync
          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(values);
              }

              return this.inspectionOnlineService.updateInspection(inspection).pipe(
                switchMap((inspection) => {
                  const schema = new InspectionSchemaUpdate();
                  schema.setInspection(this.tenantId, inspection);
                  return this.inspectionDbService.updateInspection(schema);
                })
              );
            })
          );
        })
      )
      .pipe(
        switchMap((_) => {
          // update image local path
          const termLocations = this.utils.getLocations(subjectModel, null);
          images.forEach((image) => {
            const locationName = this.getLocationFolder(termLocations, image.subjectLocationId);
            image.localFilePath = this.getLocalFilePath(inspectionSchema.title, locationName, image.fileName, "");
          });

          return this.inspectionImageDbService.putList(images);
        })
      )
      .pipe(switchMap((_) => this.getInspection(inspection.id)));
  }

  // IS05 DELETE
  deleteInspection(id: string): Observable<any> {
    return this.inspectionDbService.getInspection(id, false).pipe(
      mergeMap((inspection) => {
        const deleteFolder: string = `inspections\\${inspection.title}`;
        const measurementFolder: string = `inspections\\${id}`;

        return zip(
          this.inspectionDbService.deleteInspection(id).pipe(switchMapTo(this.uploadManagementDbService.deleted(id, UploadDataType.Inspection))),
          this.inspectionImageDbService.deleteALL(id).pipe(
            mergeMap((inspectionImageIds) => this.stereoMeasurementImageOfflineService.deleteMeasurementIntermediateInfos(id, inspectionImageIds)),
            switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionImageInfo))
          ),
          from(this.fileSysAccessService.deleteFolder(deleteFolder))
            .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionImageFile)))
            .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionModifiedImageFile)))
            .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionMeasurementImageFile))),
          defer(() => this.fileSysAccessService.deleteFolder(measurementFolder)),
          this.individualReportDbService.deleteALL(id)
        ).pipe(switchMap((schema) => this.releaseLiveAssetIfUnused(inspection.liveAssetId).pipe(map(() => schema))));
      }),
      switchMap((schema) => {
        if (!this.isAppOnline) {
          return of(schema);
        }

        return from(isOnlineStatus()).pipe(
          switchMap((isNetworkOnline) => {
            if (!isNetworkOnline) {
              return of(schema);
            }

            return this.inspectionOnlineService.deleteInspection(id).pipe(
              switchMap(() => {
                return zip(
                  this.inspectionDbService.deleteInspection(id, false),
                  this.inspectionImageDbService.deleteALL(id, false),
                  this.individualReportDbService.deleteALL(id, false),
                  this.uploadManagementDbService.deletedRelatedDataId(id, false)
                );
              })
            );
          })
        );
      })
    );
  }

  // IS07 PUT
  updateInspectionLocations(inspection: InspectionUpdateLocation): Observable<Inspection> {
    const updateSchema = new InspectionSchemaLocationUpdate();
    updateSchema.setInspectionLocation(inspection, this.userId);
    return this.inspectionDbService
      .updateInspectionLocations(updateSchema)
      .pipe(
        switchMap((schema) => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus()).pipe(
            switchMap((isNetworkOnline) => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionOnlineService.updateInspectionLocations(inspection).pipe(
                switchMap((inspection) => {
                  const schema = new InspectionSchemaUpdate();
                  schema.setInspection(this.tenantId, inspection);
                  return this.inspectionDbService.updateInspection(schema);
                })
              );
            })
          );
        })
      )
      .pipe(switchMap((schema) => this.getInspection(schema.id)));
  }

  // IS08 PUT
  updateInspectionSharedLock(inspForSharedLock: InspectionForSharedLock): Observable<Inspection> {
    throw new Error("Method cannot be executed in offline mode.");
  }

  private createInspection(insp: InspectionSchema, effectiveSubjectId: string) {
    const result = new Inspection();
    result.id = insp.id;
    result.title = insp.title;
    result.subjectModelId = insp?.subject?.subjectModel.id;
    result.subjectId = effectiveSubjectId;
    result.subjectSerialNo = insp?.subject.serialNo;
    result.inspectionDate = insp.inspectionDate;
    result.status = insp.status;
    result.inspectors = JSON.parse(insp.inspectors) as Inspector[];
    result.isValid = insp.isValid;
    result.isDeleted = insp.isDeletedFlag;
    result.permission = this.getPermission(insp);
    result.createdBy = insp.createdBy;
    result.createDate = insp.createDate;
    result.lastUpdatedBy = insp.lastUpdatedBy;
    result.lastUpdateDate = insp.lastUpdateDate;
    result.inspectionLocations = JSON.parse(insp.inspectionLocations) as InspectionLocation[];
    result.sharedLockBy = insp.sharedLockBy;
    result.schemaVersion = insp.schemaVersion;
    result.locationVersion = insp.locationVersion;
    result.attributesVersion = insp.attributesVersion;
    result.connectorTenantId = insp.connectorTenantId;

    return result;
  }

  private getPermission(insp: InspectionSchema) {
    let permission = InspectionPermission.Read;

    if (this.loginUserService.isAdminRole() || this.loginUserService.loginUser.id === insp.createdBy) {
      permission += InspectionPermission.Write;
    }

    return permission;
  }

  private getLocalFilePath(inspectionTitle: string, folderName: string, fileName: string, suffix: string): string {
    return `inspections/${inspectionTitle}/files/${folderName}/${suffix + fileName}`;
  }

  /**
   * IdentifierからLocationFileName({{ファイル番号}} + "_" + {{Identifier}})を取得
   * @param locations
   * @param locationIdentifier
   * @returns LocationFileName({{ファイル番号}} + "_" + {{Identifier}})
   */
  private getLocationFolder(locations: SubjectLocation[], locationIdentifier: string): string | null {
    for (const location of locations) {
      if (location.identifier === locationIdentifier) {
        return location.folderName;
      } else if (location.children.length > 0) {
        const childResult = this.getLocationFolder(location.children, locationIdentifier);
        if (childResult !== "Unallocated") {
          return childResult;
        }
      }
    }
    return "Unallocated";
  }
}
